/**
 * Shell UI sub-reducer — handles session/shell UI commands.
 * Extracted from editorStore.ts — pure structural refactor.
 *
 * Phase 3b PR #1 — migrated to `ShellReducerMutationResult`. Only
 * `shell/delete-entities` actually removes rows from
 * `state.drawing.entities`; every other case mutates
 * selection / activePanel / activeSpace / pickset / audit only and
 * returns `NO_SHELL_MUTATIONS` via `wrapReducerLegacy`.
 *
 * Returns the next `ShellReducerMutationResult` if the command matched,
 * or `null` if the command type is not handled here.
 */

import type {
  DynamicBlockCommand,
  EditorCommand,
  EditorShellState,
} from "@dxf-ai-editor/contracts";

import { t } from "../i18n";
import { pushAuditEvent } from "./reducerHelpers";
import {
  resolveAdjacentLayoutId,
  validateAddSpace,
  validateRemoveSpace,
  validateRenameSpace,
} from "./spaceMutationGuards";
import {
  type ShellReducerMutationResult,
  type StyleTableTouchKind,
  wrapReducerLegacy,
} from "./types";

export function reduceShellUiCommand(
  state: EditorShellState,
  command: EditorCommand | DynamicBlockCommand,
): ShellReducerMutationResult | null {
  switch (command.type) {
    case "session/switch-user":
      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            session: {
              ...state.session,
              currentUserEmail: command.userEmail,
            },
          },
          command,
          `Session switched to ${command.userEmail}.`,
          "success",
        ),
      );

    case "shell/open-panel":
      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            ui: {
              activePanel: command.panel,
            },
          },
          command,
          `Panel ${command.panel} opened.`,
          "info",
        ),
      );

    case "shell/activate-space": {
      const resolvedLayoutContext =
        command.spaceId === "model"
          ? "model"
          : (command.layoutContext ??
            (state.drawing.layoutContext === "model"
              ? "paper"
              : state.drawing.layoutContext));
      // i18n — this is the ONE activation audit row every caller
      // produces (tab strip, LAYOUT SET <name>, LAYOUT <name>), so it
      // must be bilingual like every other user-facing string. Report
      // the tab's user-visible NAME when the space is known, falling
      // back to the raw id for a space that is not in the shell
      // projection yet.
      const activatedSpaceName =
        state.drawing.spaces.find((space) => space.id === command.spaceId)
          ?.name ?? command.spaceId;

      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            drawing: {
              ...state.drawing,
              activeSpaceId: command.spaceId,
              layoutContext: resolvedLayoutContext,
            },
            selection: {
              entityIds: [],
              // Q25.C: switching spaces drops selection visually, but
              // SELECT P still recovers the prior pickset for that
              // space's content. AutoCAD behaviour: the previous-pickset
              // pointer is per-document, not per-space.
              previousPickset: state.selection.entityIds,
            },
          },
          command,
          t("audit.space_activated", { name: activatedSpaceName }),
          "success",
        ),
      );
    }

    case "shell/rename-space": {
      // LAYOUT R / LAYOUT RENAME — AutoCAD parity. Guards live in the
      // SHARED `validateRenameSpace` (spaceMutationGuards.ts) — the SAME
      // predicate the geometry-document arm evaluates, so the two
      // dispatch arms accept/reject identically (Codex P2-2):
      //   1. Cannot rename Model space (matches AutoCAD behavior).
      //   2. Target spaceId must exist and be a paper-space layout.
      //   3. newName must be non-empty after trim and ≤255 chars.
      //   4. newName must not duplicate another tab CASE-INSENSITIVELY
      //      (a pure case change of the tab's OWN name is allowed).
      // On any failure return state unchanged with a warning audit.
      const trimmedName = command.newName.trim();
      const renameRejection = validateRenameSpace(
        state.drawing.spaces,
        command.spaceId,
        trimmedName,
      );
      if (renameRejection !== null) {
        const summary =
          renameRejection === "model" || renameRejection === "not-layout"
            ? t("layout.rename.cannot_rename_model")
            : renameRejection === "not-found"
              ? t("layout.rename.notfound", { name: command.spaceId })
              : renameRejection === "empty"
                ? t("layout.rename.empty_name")
                : renameRejection === "name-too-long"
                  ? t("layout.name.too_long")
                  : t("layout.rename.duplicate_name", { name: trimmedName });
        return wrapReducerLegacy(
          pushAuditEvent(state, command, summary, "warning"),
        );
      }

      const targetSpace = state.drawing.spaces.find(
        (space) => space.id === command.spaceId,
      );
      if (!targetSpace) {
        // validateRenameSpace already rejected unknown ids; defensive
        // narrow for the type system only.
        return wrapReducerLegacy(state);
      }

      const oldName = targetSpace.name;
      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            drawing: {
              ...state.drawing,
              spaces: state.drawing.spaces.map((space) =>
                space.id === command.spaceId
                  ? { ...space, name: trimmedName }
                  : space,
              ),
            },
          },
          command,
          t("layout.rename.success", { old: oldName, new: trimmedName }),
          "success",
        ),
      );
    }

    case "shell/add-space": {
      // LAYOUT N / LAYOUT NEW — AutoCAD parity. Creates a new EMPTY
      // paper-space layout tab WITHOUT activating it (AutoCAD's LAYOUT
      // New stays on the current tab). Guards (duplicate name CI /
      // duplicate id / 255-layout cap) live in `validateAddSpace` —
      // the SAME predicate the geometry-document arm evaluates
      // (`geometryReducerLayout.ts`), so the two dispatch arms accept
      // or reject identically. NOTE (history, B2-r9): NEW is declared
      // NON-invertible — cmd-layout·history is deferred to
      // LAYOUT-MANAGE-UI-HIST; the dispatch pipeline records no entry
      // (computeInverse → null) and the redo stack is handled by the
      // standard non-invertible-mutation path.
      const trimmedName = command.name.trim();
      const rejection = trimmedName
        ? validateAddSpace(state.drawing.spaces, {
            spaceId: command.spaceId,
            name: trimmedName,
          })
        : "empty-name";
      if (rejection !== null) {
        const summary =
          rejection === "duplicate-name" || rejection === "duplicate-id"
            ? t("layout.rename.duplicate_name", { name: trimmedName })
            : rejection === "limit"
              ? t("layout.new.limit")
              : rejection === "name-too-long"
                ? t("layout.name.too_long")
                : t("layout.rename.empty_name");
        return wrapReducerLegacy(
          pushAuditEvent(state, command, summary, "warning"),
        );
      }

      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            drawing: {
              ...state.drawing,
              spaces: [
                ...state.drawing.spaces,
                {
                  id: command.spaceId,
                  kind: "layout" as const,
                  name: trimmedName,
                  // Mirrors `buildShellSpacesFromGeometryDocument`'s label
                  // so the resync (which rebuilds spaces from the geometry
                  // document) produces an identical projection.
                  pageSetup: "Imported DXF layout space",
                  viewports: [],
                },
              ],
            },
          },
          command,
          t("layout.new.success", { name: trimmedName }),
          "success",
        ),
      );
    }

    case "shell/remove-space": {
      // LAYOUT D / LAYOUT DELETE — AutoCAD parity. Guards (Model tab
      // protected, unknown id, last-remaining-layout protected) live in
      // `validateRemoveSpace` — shared with the geometry-document arm.
      // Deleting the ACTIVE layout activates the adjacent remaining
      // layout (next tab in order, else previous) in paper context.
      // NOTE (history, B2-r9): DELETE is declared NON-invertible —
      // cmd-layout·history is deferred to LAYOUT-MANAGE-UI-HIST.
      const rejection = validateRemoveSpace(
        state.drawing.spaces,
        command.spaceId,
      );
      if (rejection !== null) {
        const summary =
          rejection === "model" || rejection === "not-layout"
            ? t("layout.delete.cannot_delete_model")
            : rejection === "last-layout"
              ? t("layout.delete.last_layout")
              : t("layout.rename.notfound", { name: command.spaceId });
        return wrapReducerLegacy(
          pushAuditEvent(state, command, summary, "warning"),
        );
      }

      const removedSpace = state.drawing.spaces.find(
        (space) => space.id === command.spaceId,
      );
      const removedName = removedSpace?.name ?? command.spaceId;
      const nextActiveSpaceId =
        state.drawing.activeSpaceId === command.spaceId
          ? resolveAdjacentLayoutId(state.drawing.spaces, command.spaceId)
          : state.drawing.activeSpaceId;
      const activeChanged =
        nextActiveSpaceId !== state.drawing.activeSpaceId;

      // Entity previews of the removed space disappear with it. The
      // authoritative cache/index invalidation happens through the
      // geometry arm's document diff (`diffDocumentMutations` reports
      // every entity of a vanished space as `removed`, and
      // `commitMutation` drops them from the compact cache + metadata
      // index); the parity mutations here mirror `shell/delete-entities`.
      const removedEntries: Array<{ spaceId: string; entityId: string }> = [];
      const removedEntityIds = new Set<string>();
      for (const entity of state.drawing.entities) {
        if (entity.spaceId === command.spaceId) {
          removedEntries.push({
            spaceId: entity.spaceId,
            entityId: entity.id,
          });
          removedEntityIds.add(entity.id);
        }
      }

      const nextState = pushAuditEvent(
        {
          ...state,
          drawing: {
            ...state.drawing,
            spaces: state.drawing.spaces.filter(
              (space) => space.id !== command.spaceId,
            ),
            entities: state.drawing.entities.filter(
              (entity) => entity.spaceId !== command.spaceId,
            ),
            activeSpaceId: nextActiveSpaceId,
            // The removed tab was a paper-space layout; its adjacent
            // replacement is one too, so the restored context is paper.
            layoutContext: activeChanged
              ? "paper"
              : state.drawing.layoutContext,
          },
          // Selection entries pointing into the deleted space are gone;
          // a same-space activation switch additionally drops the whole
          // selection (mirrors shell/activate-space). Q25.C: snapshot
          // the prior pickset either way — but Codex P2-4 (PR #311): the
          // snapshot must NOT retain ids of entities that died with the
          // removed space, or SELECT Previous resurrects deleted ids
          // (`shell/delete-entities` already enforces the same invariant
          // by clearing its pickset). Filter through the SAME
          // removed-entity id set used for the live selection below.
          selection: activeChanged
            ? {
                entityIds: [],
                previousPickset: state.selection.entityIds.filter(
                  (id) => !removedEntityIds.has(id),
                ),
              }
            : {
                entityIds: state.selection.entityIds.filter(
                  (id) => !removedEntityIds.has(id),
                ),
                previousPickset: state.selection.previousPickset.filter(
                  (id) => !removedEntityIds.has(id),
                ),
              },
        },
        command,
        t("layout.delete.success", { name: removedName }),
        "success",
      );

      if (removedEntries.length === 0) {
        return wrapReducerLegacy(nextState);
      }
      return {
        state: nextState,
        mutations: {
          added: [],
          removed: removedEntries,
          updated: [],
          styleTablesTouched: new Set<StyleTableTouchKind>(),
        },
      };
    }

    case "shell/set-layout-context":
      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            drawing: {
              ...state.drawing,
              layoutContext: command.layoutContext,
            },
          },
          command,
          `Layout context set to ${command.layoutContext}.`,
          "info",
        ),
      );

    case "shell/select-entities":
      // Q25.C: snapshot the prior pickset before overwriting. AutoCAD
      // parity — `SELECT Previous` then sees the selection that was in
      // effect before this call.
      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            ui: {
              activePanel: "properties",
            },
            selection: {
              entityIds: command.entityIds,
              previousPickset: state.selection.entityIds,
            },
          },
          command,
          `${command.entityIds.length} object(s) selected for properties inspection.`,
          "success",
        ),
      );

    case "shell/clear-selection":
      // Q25.C: clearing selection still snapshots into previousPickset
      // so SELECT Previous can recover the cleared selection — that's
      // exactly AutoCAD's behavior on Esc.
      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            selection: {
              entityIds: [],
              previousPickset: state.selection.entityIds,
            },
          },
          command,
          t("audit.selection_cleared"),
          "info",
        ),
      );

    case "selection/restore-previous":
      // Q25.C — AutoCAD `SELECT Previous`. Restore prior pickset; do
      // NOT overwrite `previousPickset` (so the recovery itself is
      // idempotent — calling SELECT P twice gets the same result).
      return wrapReducerLegacy(
        pushAuditEvent(
          {
            ...state,
            ui: {
              activePanel: "properties",
            },
            selection: {
              entityIds: state.selection.previousPickset,
              previousPickset: state.selection.previousPickset,
            },
          },
          command,
          `${state.selection.previousPickset.length} object(s) restored from previous pickset.`,
          "success",
        ),
      );

    case "shell/delete-entities": {
      const entityIdsToDelete = new Set(command.entityIds);
      // Build mutations.removed from entities that ACTUALLY exist in
      // the current preview. Skipping ghost ids (already deleted) keeps
      // commitMutation's index drop list clean — see plan §"Edge cases".
      const removedEntries: Array<{ spaceId: string; entityId: string }> = [];
      for (const entity of state.drawing.entities) {
        if (entityIdsToDelete.has(entity.id)) {
          removedEntries.push({ spaceId: entity.spaceId, entityId: entity.id });
        }
      }

      if (removedEntries.length === 0) {
        return wrapReducerLegacy(
          pushAuditEvent(
            state,
            command,
            t("audit.delete_skipped_no_match"),
            "warning",
          ),
        );
      }

      const nextState = pushAuditEvent(
        {
          ...state,
          drawing: {
            ...state.drawing,
            entities: state.drawing.entities.filter(
              (entity) => !entityIdsToDelete.has(entity.id),
            ),
          },
          // Q25.C: deleted entities are gone, so the prior pickset
          // (which referenced them) becomes meaningless. Clear both
          // selection and previousPickset on delete.
          selection: {
            entityIds: [],
            previousPickset: [],
          },
        },
        command,
        `${removedEntries.length} object(s) deleted from the current drawing.`,
        "success",
      );

      return {
        state: nextState,
        mutations: {
          added: [],
          removed: removedEntries,
          updated: [],
          styleTablesTouched: new Set<StyleTableTouchKind>(),
        },
      };
    }

    // PR-9 — Shell-only dialog-open events. These actions don't mutate
    // the document or the shell state; they only emit an audit trail
    // entry. The actual dialog state lives in App.tsx local React
    // useState, opened either via the AnnotationStatusBar callback (for
    // the SCALELISTEDIT button) or via a `state.commandLine.lastSubmitted`
    // listener (for the SCALELISTEDIT command-line surface — same
    // pattern as RENAME / PURGE — see App.tsx).
    case "system/open-scalelist-dialog":
      return wrapReducerLegacy(
        pushAuditEvent(state, command, t("audit.scalelistedit_opened"), "info"),
      );

    case "system/open-annotative-object-scale-dialog":
      return wrapReducerLegacy(
        pushAuditEvent(
          state,
          command,
          t("audit.annotative_object_scale_opened", {
            entityId: command.entityId,
          }),
          "info",
        ),
      );

    // ── COLLAB-LOCK-COMMAND — LOCK / UNLOCK / LOCK LIST ──────────────────
    // This reducer stays PURE (no I/O, no store imports — CLAUDE.md
    // reducer discipline): it only ACKNOWLEDGES that a request/release/list
    // was SUBMITTED (NO_MUTATIONS — a soft lock is session state, never a
    // document mutation). The actual `useCollabLockStore` network call +
    // its eventual grant/deny/list report happen as a side effect in the
    // SAME `routeLayoutCommand` CLI-intercept block (editorStore.ts) that
    // constructs this typed command, exactly mirroring how DIMSTYLE / LAYOUT
    // keep their reducer arm audit-only and their side effect co-located
    // with the CLI parse.
    case "collab/lock-request":
      return wrapReducerLegacy(
        pushAuditEvent(
          state,
          command,
          t("collab.lock.requested", { count: command.scopes.length }),
          "info",
        ),
      );

    case "collab/lock-release":
      return wrapReducerLegacy(
        pushAuditEvent(
          state,
          command,
          t("collab.lock.released", { count: command.scopes.length }),
          "info",
        ),
      );

    case "collab/list-locks":
      // The actual listing (per-lock scope/target/owner rows, or the
      // "not connected" message) is formatted by the CLI-intercept side
      // effect from the LIVE `useCollabLockStore` snapshot — this
      // acknowledgement only records that the query ran.
      return wrapReducerLegacy(
        pushAuditEvent(state, command, t("collab.lock.list_query"), "info"),
      );

    default:
      return null;
  }
}
